<template>
  <div
    class="column no-wrap items-stretch justify-between"
    :class="{
      'q-mx-auto': $register,
      'q-px-md': !$register && !$q.screen.lt.sm && $isLandscapeView(),
    }"
  >
    <q-inner-loading :showing="loading" />
    <transition
      v-if="$register || $q.screen.lt.sm"
      leave-active-class="animated fadeOutUp"
      enter-active-class="animated fadeInDown"
    >
      <q-banner
        v-if="deliveryMessage"
        :rounded="$q.screen.gt.xs"
        dense
        class="bg-info"
      >
        <template #avatar>
          <q-icon
            name="fas fa-location-dot"
            :color="$getColor(currentSite, 'info')"
            size="md"
            class="q-mt-xs"
          />
        </template>
        <div
          class="row text-left col-auto self-center"
          :class="$getTextColor(currentSite, 'info')"
        >
          <div class="column">
            {{ $t('checkout.delivery.deliveryTo') }} <span class="text-h6"> {{ deliveryMessage }}</span>
          </div>
        </div>
      </q-banner>
    </transition>

    <div
      class="q-py-md q-px-sm row q-gutter-md content-start scroll justify-start"
    >
      <q-card
        v-if="redeemableVoucherTypes.length"
        class="column justify-between cursor-pointer bg-primary rounded-borders"
        :class="{ 'justify-center': !currentSite.imageUrl }"
        style="flex-grow: 0; flex-shrink: 1;"
        :style=" { width: width }"
        flat
        bordered
        @click="openRewardsDialog()"
      >
        <q-card-section
          class="column justify-center q-pa-sm bg-responsive q-ma-sm"
          :class="{
            'justify-between': !!currentSite.rewardCurrencyImageUrl,
            'justify-center': !currentSite.rewardCurrencyImageUrl
          }"
          style="border-radius: 8px; min-height: 80px; box-shadow: 0 3px 5px -1px #0003, 0 6px 10px #00000024, 0 1px 18px #0000001f;"
        >
          <q-img
            v-if="!!currentSite.rewardCurrencyImageUrl"
            :src="currentSite.rewardCurrencyImageUrl"
            class="q-mx-auto"
            :style="{ 'max-width': $q.screen.lt.sm ? '80px' : '100px' }"
          />
          <div
            class="text-h6 text-center q-mt-sm q-mx-sm"
            style="line-height: 1.2;"
          >
            {{ currentCircuit.rewardsCurrencyName }} {{ $t('pages.redemptions.redemptions') }}
          </div>
        </q-card-section>
        <q-card-section class="text-white q-py-none q-pb-sm text-center">
          <div
            class="text-caption"
            :class="$getTextColor(currentSite, 'primary')"
            style="line-height: 1.2;"
            :style="{ 'font-size': $q.screen.lt.sm ? '11px' : undefined }"
          >
            {{ `${order.availablePoints} ${currentCircuit.rewardsCurrencyName} ${$t('status.available')}` }}
          </div>
        </q-card-section>
      </q-card>
      <q-card
        v-for="category in allCategories"
        :key="category.id"
        class="column cursor-pointer overflow-hidden"
        :class="{ 'justify-center': !category.imageUrl }"
        style="min-height: 50px;"
        :style=" {
          border: itemCount(category) ? '3px solid var(--q-primary)' : undefined,
          width: width
        }"
        flat
        bordered
        @click="openCategoryDialog(category)"
      >
        <div>
          <transition
            leave-class="hidden"
            enter-active-class="animated bounceIn"
          >
            <q-badge
              v-if="itemCount(category)"
              :key="itemCount(category)"
              style="font-size: 18px; z-index: 1"
              class="q-py-sm text-bold"
              color="primary"
              :text-color="$isColorDark(currentSite.primaryColor) ? 'white' : 'black'"
              floating
            >
              x {{ itemCount(category) }}
            </q-badge>
          </transition>
        </div>
        <q-img
          v-if="!!category.imageUrl"
          :src="category.imageUrl"
        />

        <q-card-section class="q-pa-xs">
          <div class="text-body2 text-center text-weight-bolder">
            {{ category.name }}
          </div>
        </q-card-section>
      </q-card>
    </div>
    <featured-items
      v-if="!$_.isEmpty(featuredItems)"
      :class="{ 'q-px-sm': $q.screen.gt.sm }"
      :order="order"
      :featured-items="featuredItems"
      :dialog="false"
      available-online
    />

    <category-dialog
      ref="categoryDialog"
      :order="order"
      available-online
    />

    <rewards-dialog
      ref="rewardsDialog"
      available-online
    />
  </div>
</template>

<script>
import AvailableItemsMixin from 'src/modules/checkout/components/mixins/AvailableItemsMixin'
import MembershipVoucherTypesMixin from 'src/modules/checkout/components/mixins/MembershipVoucherTypesMixin'
import CategoryDialog from 'src/modules/checkout/dialogs/CategoryDialog'
import FeaturedItems from 'src/modules/checkout/dialogs/FeaturedItems'
import RewardsDialog from 'src/modules/checkout/dialogs/RewardsDialog'
import SwiperModulesMixin from 'src/modules/consumer/components/mixins/SwiperModulesMixin'
import { watchEffect } from 'vue'
export default {
  name: 'Items',
  components: {
    CategoryDialog,
    FeaturedItems,
    RewardsDialog
  },
  mixins: [SwiperModulesMixin, MembershipVoucherTypesMixin, AvailableItemsMixin],
  inject: ['order', 'checkoutData'],
  props: {
    emitAction: {
      type: Boolean,
      default: false,
      required: false
    },
    hideCheckoutControls: {
      type: Boolean,
      default: false,
      required: false
    }
  },
  emits: ['next'],
  data () {
    return {
      isActive: false,
      loading: true
    }
  },
  context: ['currentUser'],
  configuration: ['currentCircuit', 'currentSite', 'deliveryLocations', 'items', 'voucherTypes'],
  computed: {
    allCategories () {
      const availableOnlineCategories = _.compact([
        this.currentUser?.itemPreferencesAsCategory,
        ...(this.currentSite.hasOnlineDelivery
          ? this.order.availableOnlineCategories
          : _.filter(this.order.availableOnlineCategories, { hasAvailableOnlineReadyToServeItems: true })
        )
      ])
      // Filter categories to only include those with available items
      return _.filter(availableOnlineCategories, category => {
        // Get all items belonging to this category
        return _.some(category.items?.data, item => this.isItemAvailable(item))
      })
    },

    allItemCount () {
      return _.filter(this.order.orderItems.data, { type: 'item' }).length
    },
    redeemableVoucherTypes () {
      if (!this.currentUser) {
        return []
      }

      return this.membershipVoucherTypes
    },

    deliveryMessage () {
      const messages = []
      if (this.order.deliveryType) {
        messages.push(_.startCase(this.order.deliveryTypeName))
      }

      if (this.order.deliveryLocation) {
        messages.push(this.order.deliveryLocation)
      }

      if (this.order.deliveryPositions.length) {
        messages.push(this.order.deliveryPositions.join(', '))
      }

      if (this.order.deliveryDetails) {
        messages.push(this.order.deliveryDetails)
      }

      return messages.join(' - ')
    },
    featuredItems () {
      return _.filter(this.items.data, item => {
        if (!item.active) return false

        if (!item.isFeature) return false

        if (!this.$consumer) return true

        if (!item.availableOnline) return false

        if (!this.currentSite.hasOnlineDelivery && item.requiresPreparation) return false

        return this.isItemAvailable(item)
      })
    },
    width () {
      if (this.$q.screen.xs) {
        return '45%'
      } else if (this.$q.screen.sm) {
        return '23%'
      } else if (this.$q.screen.md) {
        return '190px'
      } else {
        return '210px'
      }
    }
  },
  mounted () {
    this.stopWatch = watchEffect(() => {
      this.checkoutData.showLoading = this.loading
      this.checkoutData.purchasingMembership = false

      this.checkoutData.stepTitle = this.$i18n.t('checkout.steps.foodAndDrink')
      this.checkoutData.showMovieCard = true
      this.checkoutData.showCartControls = true
      this.checkoutData.showCheckoutFooter = true

      this.checkoutData.expandDefault = false
      this.checkoutData.expandConcessions = true
      this.checkoutData.expandTickets = false

      this.checkoutData.infoTitle = this.$i18n.t('checkout.infoTitles.concessions')
      this.checkoutData.info = this.$i18n.tc('item.itemCount', this.allItemCount)
      this.checkoutData.showInfo = true
      this.checkoutData.showSubtotal = true
      this.checkoutData.showTotals = true

      this.checkoutData.showEmptyCartNextLabel = false

      this.checkoutData.canProceed = this.order.orderItems.data.length !== 0
    })
  },
  async created () {
    this.processQueryParameters()

    // There is some strange watcher/computed timing issues after processing the query parameters
    // Use nextTick and order computed properties directly solve the issue
    await this.$nextTick()
    const anyAvailableCategories = _.some(_.compact([this.currentUser?.itemPreferencesAsCategory, ...this.order.availableOnlineCategories]))
    // If items are not available, then remove this page from the router history
    // If we don't remove this from the router history, then the user will not be able to navigate back to seats or showing
    // The user would instead be directed here, which would automatically redirect them back to the cart
    if (!this.currentSite.allowItemOrdersOnline && !this.order.nextShowing) {
      // If online item orders are not allowed for this site and selected showing, then return to home
      this.$router.replace('/').catch(() => {})
    } else if ((!anyAvailableCategories && !this.featuredItems.length) || (this.order.nextShowing && !this.order.nextShowing.allowItemOrdersOnline)) {
      // If no categories are available, then skip this page
      this.$router.replace('/checkout/cart')
    } else {
      this.loading = false
    }
  },
  unmounted () {
    this.stopWatch && this.stopWatch()
  },
  methods: {
    processQueryParameters () {
      if (this.$route.query['delivery-type']) {
        this.order.deliveryType = this.$route.query['delivery-type']
      }
      if (this.$route.query['delivery-location']) {
        this.order.deliveryLocation = decodeURIComponent(this.$route.query['delivery-location'])
      } else if (this.$route.query['table-name']) {
        this.order.deliveryLocation = this.$route.query['table-name']
      } else if (this.$route.query['seat-name']) {
        this.order.deliveryLocation = this.$route.query['seat-name'].split(' - ')[0]
        this.order.deliveryPositions = [this.$route.query['seat-name'].split(' - ')[1]]
      }
      if (this.$route.query['delivery-position']) {
        const deliveryPosition = decodeURIComponent(this.$route.query['delivery-position'])
        this.order.addDeliveryPosition(deliveryPosition)
      }
      if (this.$route.query['delivery-details']) {
        this.order.deliveryDetails = decodeURIComponent(this.$route.query['delivery-details'])
      }
      if (this.$route.query['name-for-order']) {
        this.order.nameForOrder = decodeURIComponent(this.$route.query['name-for-order'])
      }
    },
    openCategoryDialog (category) {
      this.$refs.categoryDialog.show(category)
      this.$refs.rewardsDialog.hide()
      this.isActive = category
    },
    itemCount (category) {
      return _(this.order.orderItems.data).map('itemOrderItem.itemId').filter(itemId => _.includes(category.itemIds, itemId)).value().length
    },
    openRewardsDialog () {
      this.$refs.rewardsDialog.show()
      this.$refs.categoryDialog.hide()
      this.isActive = 'rewards'
    },
    next () {
      this.$emit('next')
    }
  },
  trackPage () {
    return {
      showingId: (this.order?.showing && this.order.showing.id) || undefined,
      // Append additional tracking metadata for the checkout flow
      ...this.checkoutData.trackingData
    }
  }
}
</script>

<style lang="scss" scoped>
.swiper-button-next:after, .swiper-button-prev:after{
  font-size: 18px;
}
.swiper-button-next, .swiper-button-prev{
  width: 36px;
  height: 36px;
  border-radius: 0px;
}
.swiper-button-disabled{
  display: none;
}
.active{
  position: relative;
  margin-bottom: 10px;
}
.active:before{
  display: block;
  content: "";
  height: calc(100% - 6px);
  width: calc(100% - 6px);
  border: 3px solid var(--q-primary);
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
}
.wrapper {
  margin-bottom: 10px;
}
.wrapper .active:after{
  content: "";
  border-left-width: 8px;
  border-left-style: solid;
  border-left-color: transparent;
  border-right-width: 8px;
  border-right-style: solid;
  border-right-color: transparent;
  border-top-width: 8px;
  border-top-style: solid;
  border-top-color: var(--q-primary);
  position: absolute;
  z-index: 2;
  bottom: -8px;
  left: 45%;
}

.border-primary-3{
  position: relative;
}
.border-primary-3:before{
  display: block;
  content: "";
  height: calc(100% - 6px);
  width: calc(100% - 6px);
  border: 3px solid var(--q-primary);
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: 1;
}

</style>
